import { TemplateDto, getTemplateAccentColor, isTemplateSceneV2 } from "@exibra/shared";

/**
 * Layout orientation a template renders for.
 * - `horizontal`  — a single 16:9 landscape layout (all legacy v1 templates)
 * - `vertical`    — a single portrait layout
 * - `responsive`  — a v2 template carrying both a primary and an alternate layout
 */
export type TemplateOrientation = "horizontal" | "vertical" | "responsive";

/**
 * Derives the {@link TemplateOrientation} from a template's scene. v1 templates are always
 * treated as horizontal; v2 templates report their primary orientation, or `responsive` when
 * they additionally carry an alternate-orientation layout.
 */
export function getTemplateOrientation(template: TemplateDto): TemplateOrientation {
    const scene = template.scene;
    if (isTemplateSceneV2(scene)) {
        if (scene.alternate) return "responsive";
        return scene.orientation === "vertical" ? "vertical" : "horizontal";
    }
    return "horizontal";
}

/**
 * The major version of a template, defaulting to 1 for legacy records without an explicit version.
 */
export function getTemplateVersion(template: TemplateDto): 1 | 2 {
    return template.version === 2 ? 2 : 1;
}

/**
 * Picks the background and accent swatch colors used by the compact list thumbnail. The background
 * falls back to `null` (callers render a neutral tile) while the accent always resolves to a hex
 * value via the shared helper.
 */
export function getTemplateThumbColors(template: TemplateDto): {
    background: string | null;
    accent: string;
} {
    return {
        background: template.scene?.style?.backgroundColor ?? null,
        accent: getTemplateAccentColor(template) ?? "#d0f435",
    };
}
